import CashFlowChart from "./CashFlowChart";
import PaymentDonut from "./PaymentDonut";
import { formatIDR } from "@/lib/constants";
import { ShieldAlert } from "lucide-react";

const MONTH_ID = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const fmtPeriod = (k) => {
  const m = /^(\d{4})-(\d{2})$/.exec(k || "");
  return m ? `${MONTH_ID[Number(m[2]) - 1]} ${m[1]}` : k; // "Jan 2026" utk bulanan; mingguan sudah "2026-W11"
};

/**
 * CashFlowTab — Fase 3. Dua grafik utama (mingguan + bulanan) dengan tabel
 * rekap + donut Progress Pembayaran (2026-06-20, user request). Tanpa pie
 * kategori pengeluaran / rencana vs realisasi sesuai spec user.
 */
function RecapTable({ title, rows, testid }) {
  return (
    <div className="bg-white border border-slate-200" data-testid={testid}>
      <div className="px-4 py-2.5 border-b border-slate-200"><h3 className="text-sm font-semibold text-[#0B1D3A]">{title}</h3></div>
      {(!rows || rows.length === 0) ? (
        <div className="p-5 text-center text-[11px] text-slate-400">Belum ada data.</div>
      ) : (
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left">{["Periode", "Uang Masuk", "Uang Keluar", "Net"].map((h) => <th key={h} className="px-3 py-2 overline text-slate-600">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-b border-slate-100">
                <td className="px-3 py-1.5 tabular-nums text-slate-600">{fmtPeriod(r.key)}</td>
                <td className="px-3 py-1.5 tabular-nums text-emerald-700">{formatIDR(r.in || 0)}</td>
                <td className="px-3 py-1.5 tabular-nums text-[#0B1D3A]">{formatIDR(r.out || 0)}</td>
                <td className={`px-3 py-1.5 tabular-nums font-semibold ${(r.net || 0) < 0 ? "text-rose-700" : "text-[#0B1D3A]"}`}>{formatIDR(r.net || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function CashFlowTab({ mon, canViewFinance = true }) {
  if (!canViewFinance) return (
    <div className="bg-white border border-slate-200 p-6 text-center" data-testid="cf-no-access">
      <ShieldAlert size={20} className="mx-auto text-slate-300 mb-2" />
      <div className="text-xs text-slate-500">Anda tidak memiliki akses melihat arus kas (butuh permission Laporan Keuangan → Lihat).</div>
    </div>
  );
  const s = mon?.summary || {};
  return (
    <div className="space-y-4" data-testid="cash-flow-tab">
      {/* 2026-06-20 (user request): DUA metrik progress pembayaran terpisah —
          fungsi bersama, label menyebut dasar, tanpa campur denominator. */}
      <div className="bg-white border border-slate-200 p-4" data-testid="cf-progress-pembayaran">
        <h3 className="text-sm font-semibold text-[#0B1D3A] mb-3">Progress Pembayaran</h3>
        {(() => {
          // 2026-06-21 (user report): SATU sumber dgn Ringkasan/Keuangan —
          // payment_breakdown (bukan payment_progress lama).
          const b = s.payment_breakdown || {};
          const receiptRemain = b.receipt_basis_value != null
            ? Math.round((b.receipt_basis_value - (b.received_total || 0)) * 100) / 100
            : null;
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div data-testid="cf-donut-receipt-basis">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">
                  Penerimaan terhadap Nilai Proyek{b.receipt_basis_label ? ` — dasar: ${b.receipt_basis_label}` : ""}
                </div>
                <PaymentDonut pct={b.official_payment_progress ?? null} received={Number(b.received_official ?? 0)}
                  remaining={b.official_collectible_amount != null ? Math.max(0, Number(b.official_collectible_amount) - Number(b.received_official || 0)) : 0}
                  basisLabel="Kas Harus Diterima Resmi" over={!!b.receipt_over_official}
                  receivedLabel="Diterima Resmi" testid="donut-cashflow-receipt" />
                  remaining={receiptRemain} basisLabel={b.receipt_basis_label} over={!!b.receipt_over} testid="cf-donut-receipt" />
              </div>
              <div data-testid="cf-donut-invoice-settlement">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">Pelunasan Invoice — dasar: Kas Harus Diterima</div>
                <PaymentDonut pct={b.invoice_settlement_pct ?? null} received={Number(b.received_total ?? s.total_income ?? 0)}
                  remaining={b.overdue_receivable ?? 0} basisLabel="Kas Harus Diterima" over={!!b.invoice_settlement_over}
                  remainingLabel="Sisa Piutang Invoice" testid="cf-donut-invoice" />
              </div>
            </div>
          );
        })()}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <CashFlowChart title="Arus Kas Mingguan" data={mon?.cash_flow_weekly} storageKey="weekly" testid="cf-weekly-chart" />
        <CashFlowChart title="Arus Kas Bulanan" data={mon?.cash_flow_monthly} storageKey="monthly" testid="cf-monthly-chart" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <RecapTable title="Rekap Mingguan" rows={mon?.cash_flow_weekly} testid="cf-weekly-table" />
        <RecapTable title="Rekap Bulanan" rows={mon?.cash_flow_monthly} testid="cf-monthly-table" />
      </div>
    </div>
  );
}
